import { useLayout } from '@/layout/LayoutProvider';
import { Checkbox as CheckboxGroup } from '@storeblocks/checkbox';
import { Checkboxes as RebrandedCheckboxes } from '@storeblocks/checkboxes';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { ChangeEvent } from 'react';
import { FieldSet } from '../../shared/components/Form/Form';
import {
  ArraySchema,
  BooleanSchema,
  ErrorMessage,
  Errors,
  FieldProps,
  ObjectSchema,
  Schema,
  StringSchema,
  isArraySchema,
  isBooleanSchema,
  isObjectSchema,
  isStringSchema
} from '../types';
import { joinErrors, orderProperties, zip } from '../validation/utils';
import { hasErrors } from '../validation/validateSchema';

type MultipleChoiceFieldProps = FieldProps<
  ArraySchema<StringSchema> | ObjectSchema<{ [key: string]: BooleanSchema }>
>;

/**
 * `<MultipleChoiceField />` renders a schema as a checkbox group
 *
 * The widget can operate on two kinds of schemas
 *
 * ### `ArraySchema` with enumerable `string` values
 *
 * ```
 * {
 *   "type": "array",
 *   "items": {"type": "string", enum: ["A", "B", "C"]},
 *   "minItems": 3
 * }
 * ```
 *
 * ### `ObjectSchema` with `boolean` properties
 *
 * Note that you _must_ specify `enum: [true]` for each property in order
 * to be able to verify that the property has been checked since specificying
 * `required` on the object itself only asserts that the
 * property has a value (true or false is both valid, null is not)
 *
 * ```
 * {
 *   "type": "object",
 *   "properties": {
 *     "a": {"type": "boolean", enum: [true]},
 *     "b": {"type": "boolean", enum: [true]},
 *     "c": {"type": "boolean", enum: [true]}
 *   }
 * }
 * ```
 */
export const MultipleChoiceField = ({
  onChange,
  id,
  name,
  value = [],
  errors,
  forceShowError,
  schema,
  readOnly
}: MultipleChoiceFieldProps) => {
  const { getText } = useTranslations();
  const { isRebrand } = useLayout();

  if (!isValidSchema(schema)) {
    throw new Error(
      `MultipleChoiceField must have be an ArraySchema OR ObjectSchema. ${JSON.stringify(
        schema
      )} given.`
    );
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = Array.isArray(value)
      ? addOrRemove(value, e.target.value, e.target.checked)
      : {
          ...value,
          [e.target.value]: e.target.checked
        };

    return onChange({
      target: {
        name,
        value: newValue as any
      }
    });
  };

  // Special logic to handle consents error message
  const schemaErrors = schema.$uiOptions?.errorMessages;
  const consentsError =
    schemaErrors &&
    Object.values(schemaErrors).find(schemaError =>
      schemaError.includes('consents')
    );

  const showError = !!(forceShowError && errors);
  const generalErrors = getGeneralErrors(
    errors,
    Array.isArray(value),
    consentsError
  );

  const options = !isObjectSchema(schema)
    ? getOptions(schema).map(([enumValue, label]) => {
        return { id: `${id}_${enumValue!}`, label: label!, value: enumValue };
      })
    : getOptions(schema).map(enumValue => {
        return {
          id: `${id}_${enumValue[0]!}`,
          label: enumValue[1]!,
          value: enumValue[0]
        };
      });

  const mappedValue: string[] = !Array.isArray(value)
    ? Object.keys(value).filter(key => value[key])
    : Object.values(value).map(val => val as string);

  const error =
    showError && generalErrors.length > 0
      ? joinErrors(generalErrors, getText)
      : '';

  return isRebrand ? (
    <FieldSet>
      <RebrandedCheckboxes
        groupName={id}
        legendProps={{
          hint: schema.$uiOptions?.hint,
          title: schema.title ?? ''
        }}
        options={options.map(option => ({
          ...option,
          defaultChecked: mappedValue.includes(option.value)
        }))}
        disabled={readOnly}
        onChange={handleChange}
      />
    </FieldSet>
  ) : (
    <FieldSet>
      <CheckboxGroup
        id={id}
        label={schema.title}
        hint={schema.$uiOptions?.hint}
        options={options}
        name={name}
        disabled={readOnly}
        onChange={handleChange}
        value={mappedValue}
        error={error}
      />
    </FieldSet>
  );
};

const getGeneralErrors = (
  errors: Errors<Schema>,
  isArray: boolean,
  consentsError?: string
): Array<ErrorMessage> => {
  if (!hasErrors(errors)) {
    return [];
  }

  if (isArray) {
    return (errors as Errors<ArraySchema>).all;
  }

  if (consentsError) {
    return [consentsError];
  }

  return ['errors.multipleChoiceField.invalid'];
};

const isValidSchema = (schema: Schema) => {
  return !!(
    (isArraySchema(schema) &&
      isStringSchema(schema.items) &&
      schema.items.enum) ||
    (isObjectSchema(schema) &&
      Object.values(schema.properties || {}).every(isBooleanSchema))
  );
};

const addOrRemove = (
  array: Array<string | null | undefined>,
  value: string | null | undefined,
  add: boolean
) => {
  const result = new Set(array);
  if (add) {
    return [...result.add(value)];
  }
  result.delete(value);
  return [...result];
};

const getOptions = (
  schema: ArraySchema | ObjectSchema
): Array<[string, string | undefined]> => {
  if (
    isArraySchema(schema) &&
    isStringSchema(schema.items) &&
    schema.items.enum
  ) {
    return zip(schema.items.enum!, schema.items.enumNames!) as any;
  }
  if (isObjectSchema(schema)) {
    return orderProperties(
      Object.keys(schema.properties || {}),
      (schema.$uiOptions || {}).propertyOrder
    ).map(property => [property, schema.properties![property].title]);
  }
  return [];
};
